import * as React from "react";
import { useState, useMemo } from "react";
import { BaseType } from "../../../../application/domain/base/base-type";
import { connectService } from "@graffer-inc/smart-apply";
import { useMount } from "../../../../utils/custom-hooks";
import { ProcedureEntity } from "../../../../application/domain/procedures/procedure-entity";
import {
  ProcedureMailCfgContextProps,
  ProcedureMailCfgService,
} from "../../../../application/service/smart-apply/procedure-detail/procedure-mail-cfg-service";
import {
  MailTemplateEditor,
  AttributesMappingType,
} from "./mail-template-editor";
import classNames from "classnames";

export const MailStatus = [
  "RECIEVED",
  "REJECTED",
  "WITHDRAWN",
  "COMPLETED",
] as const;
export type MailStatusLiterals = (typeof MailStatus)[number];
export type MailStatusMatcher<T> = { [key in MailStatusLiterals]: T };

class MailTmplUIState extends BaseType<MailStatusLiterals> {
  static recievedTmpl() {
    return new MailTmplUIState("RECIEVED");
  }
  static rejectedTmpl() {
    return new MailTmplUIState("REJECTED");
  }
  static withdrawnTmpl() {
    return new MailTmplUIState("WITHDRAWN");
  }
  static completedTmpl() {
    return new MailTmplUIState("COMPLETED");
  }
}

export const MailCfg: React.FC<ProcedureMailCfgContextProps> = ({
  procedure,
  orgName,
  mailCfg,
  defaultTemplates,
  footer,
  onMountSetting,
  onTemplateEdit,
  onTestSend,
  onSave,
}) => {
  const [uiState, setUiState] = useState<MailTmplUIState>(
    MailTmplUIState.recievedTmpl(),
  );
  const sampleAttributes = {
    OrgName: orgName,
    ProcedureName: procedure?.name,
    OrderUrl:
      "https://ttzk.graffer.jp/smart-apply/applications/0000000000000000000 ", // add half space so that link will not be connected with subsequent strings
    ApplicationId: "0000-0000-0000-0000000",
    CreatedAt: "2020-05-25 12:35:52",
    CreatedDate: "2020-05-25",
    UpdatedAt: "2020-05-28 15:01:20",
    UpdatedDate: "2020-05-28",
    ApplicantName: "東 明",
    ApplicantNameKana: "ヒガシ アキラ",
    Message:
      "（※{メッセージ}のエリアには、申請のステータス更新（申請一覧＞基本情報＞ステータスを更新）で、「申請者へのメッセージ」に入力した内容が挿入されます。「申請者へのメッセージ」を入力しない場合には何も挿入されません。）",
  };

  useMount(async () => {
    await onMountSetting(procedure!);
  });
  const isValidCfg = useMemo(() => mailCfg?.isValid() ?? false, [mailCfg]);

  if (!(procedure && mailCfg && defaultTemplates && orgName)) return <></>;
  return (
    <>
      <div className="columns">
        <div className="column is-2">
          <nav className="custom-menu-list menu">
            <p className="menu-label">通知対象</p>
            <ul className="menu-list">
              <li>
                <a
                  className={classNames({
                    "is-active": uiState.equals("RECIEVED"),
                  })}
                  href="#/"
                  onClick={() => setUiState(MailTmplUIState.recievedTmpl())}
                >
                  申請受付通知
                </a>
              </li>
              <li>
                <a
                  className={classNames({
                    "is-active": uiState.equals("REJECTED"),
                  })}
                  href="#/"
                  onClick={() => setUiState(MailTmplUIState.rejectedTmpl())}
                >
                  差し戻し通知
                </a>
              </li>
              <li>
                <a
                  className={classNames({
                    "is-active": uiState.equals("WITHDRAWN"),
                  })}
                  href="#/"
                  onClick={() => setUiState(MailTmplUIState.withdrawnTmpl())}
                >
                  取り下げ通知
                </a>
              </li>
              <li>
                <a
                  className={classNames({
                    "is-active": uiState.equals("COMPLETED"),
                  })}
                  href="#/"
                  onClick={() => setUiState(MailTmplUIState.completedTmpl())}
                >
                  完了通知
                </a>
              </li>
            </ul>
          </nav>
        </div>
        <div className="column">
          {uiState.matchType({
            RECIEVED: (
              <MailTemplateEditor
                key={"recieved_template"}
                explanation={
                  "申請が到達したことを申請者に通知するメールの文面を設定します。"
                }
                procedure={procedure}
                onTestSend={onTestSend}
                onSave={onSave}
                isValidCfg={isValidCfg}
                attribute={AttributesMappings}
                sampleAttributes={sampleAttributes}
                initialTmpl={
                  mailCfg.recievedTemplate || defaultTemplates["RECIEVED"]
                }
                footer={footer}
                onTemplateEdit={(e) => {
                  onTemplateEdit({
                    ...mailCfg,
                    recievedTemplate: e,
                  });
                }}
              />
            ),
            REJECTED: (
              <MailTemplateEditor
                key={"rejected_template"}
                explanation={
                  "申請ステータスを差し戻しに更新した際に申請者に通知するメールの文面を設定します。"
                }
                procedure={procedure}
                onTestSend={onTestSend}
                onSave={onSave}
                isValidCfg={isValidCfg}
                attribute={[...AttributesMappings, AttributeMessage]}
                sampleAttributes={sampleAttributes}
                initialTmpl={
                  mailCfg.rejectedTemplate || defaultTemplates["REJECTED"]
                }
                footer={footer}
                onTemplateEdit={(e) => {
                  onTemplateEdit({
                    ...mailCfg,
                    rejectedTemplate: e,
                  });
                }}
              />
            ),
            WITHDRAWN: (
              <MailTemplateEditor
                key={"withdrawn_template"}
                explanation={
                  "申請ステータスを取下げに更新した際に申請者に通知するメールの文面を設定します。"
                }
                procedure={procedure}
                onTestSend={onTestSend}
                onSave={onSave}
                isValidCfg={isValidCfg}
                attribute={[...AttributesMappings, AttributeMessage]}
                sampleAttributes={sampleAttributes}
                initialTmpl={
                  mailCfg.withdrawnTemplate || defaultTemplates["WITHDRAWN"]
                }
                footer={footer}
                onTemplateEdit={(e) => {
                  onTemplateEdit({
                    ...mailCfg,
                    withdrawnTemplate: e,
                  });
                }}
              />
            ),
            COMPLETED: (
              <MailTemplateEditor
                key={"completed_template"}
                explanation={
                  "申請ステータスを完了に更新した際に申請者に通知するメールの文面を設定します。"
                }
                procedure={procedure}
                onTestSend={onTestSend}
                onSave={onSave}
                isValidCfg={isValidCfg}
                attribute={[...AttributesMappings, AttributeMessage]}
                sampleAttributes={sampleAttributes}
                initialTmpl={
                  mailCfg.completedTemplate || defaultTemplates["COMPLETED"]
                }
                footer={footer}
                onTemplateEdit={(e) => {
                  onTemplateEdit({
                    ...mailCfg,
                    completedTemplate: e,
                  });
                }}
              />
            ),
          })}
        </div>
      </div>
    </>
  );
};

const AttributesMappings: Array<AttributesMappingType> = [
  {
    tag: "{{.OrgName}}",
    alias: "{組織名称}",
    description: "申請した組織の名称",
  },
  {
    tag: "{{.ProcedureName}}",
    alias: "{手続き名称}",
    description: "申請した手続きの名称",
  },
  {
    tag: "{{.OrderUrl}}",
    alias: "{確認用URL}",
    description: "申請内容を確認できるリンク",
  },
  {
    tag: "{{.ApplicationId}}",
    alias: "{申請番号}",
    description: "申請を特定する番号",
  },
  {
    tag: "{{.CreatedAt}}",
    alias: "{申請日時}",
    description: "申請を行った日時",
  },
  { tag: "{{.CreatedDate}}", alias: "{申請日}", description: "申請を行った日" },
  {
    tag: "{{.UpdatedAt}}",
    alias: "{最終更新日時}",
    description: "申請書の最後更新日時",
  },
  {
    tag: "{{.UpdatedDate}}",
    alias: "{最終更新日}",
    description: "申請書の最後更新日",
  },
  {
    tag: "{{.ApplicantName}}",
    alias: "{申請者名}",
    description: "申請者名。法人の場合は法人名",
  },
  {
    tag: "{{.ApplicantNameKana}}",
    alias: "{申請者名カナ}",
    description: "申請者名カナ",
  },
];

const AttributeMessage: AttributesMappingType = {
  tag: "{{.Message}}",
  alias: "{メッセージ}",
  description: "申請者へのメッセージ",
  tooltip:
    "ステータス更新時に入力できるメッセージ。\n未入力の場合はメールに記載されません。",
  bodyOnly: true,
};

export const MailCfgContainer = connectService<
  { procedure?: ProcedureEntity },
  ProcedureMailCfgContextProps,
  ProcedureMailCfgService
>(
  MailCfg,
  (p) =>
    new ProcedureMailCfgService({
      procedure: p.procedure!,
    }),
);
